import React, { useContext, useEffect, useState } from 'react'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import MicroModal from 'micromodal'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { useParams } from 'react-router-dom' 
import { TeamContext } from '../../MenuTabs/MenuTabs'
import useLicenseTable, { LicenseTableProvider } from '../context/LicenseTableContext'
import LicensesTableButton from './LicensesTableButton'
import { modalManageLicenseId } from './Utils'
import ModalManageLicense from '../ManageLicense/ModalManageLicense'
import { ExpiringLicenseTableData, ExpiringLicenseTableType } from './data/ExpiringLicenseTableData'
import ManageLicense from '../../../../../orgs/ManageLicense'
import LicensesStore from '../../Organization/LicensesStore'
import Loader from '../../../../common/loader/Loader'
import Button from '../../../../common/button/Button'

const ExpiringLicensesTable = ({ licensesData, membersSelection }) => {

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([])
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => ExpiringLicenseTableData( licensesData ) )
  const { setUseHeader, refetchLicenses } = useLicenseTable()
  const [ modalLoading, setModalLoading ] = useState([])
  const [ dismissLoading, setDismissLoading ] = useState(false)

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader(['all'])
  }, [])

  const columns = React.useMemo<ColumnDef<ExpiringLicenseTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'licenseTypeID',
            accessorKey: 'licenseTypeID',
            prettyName: 'License Type ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>License type ID</span>,
          },
          {
            id: 'total',
            accessorKey: 'total',
            prettyName: 'Total',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Total</span>,
          },
          {
            id: 'name',
            accessorKey: 'name',
            prettyName: 'Name',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Name</span>,
          },
          {
            id: 'product',
            accessorKey: 'product',
            prettyName: 'Product',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Access from product</span>,
          },
          {
            id: 'expires',
            accessorKey: 'expires',
            prettyName: 'Expires',
            show: true,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Expires</span>,
          },
          {
            id: 'productID',
            accessorKey: 'productID',
            prettyName: 'Product ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>Product ID</span>,
          },
          {
            id: 'membersAssigned',
            accessorKey: 'membersAssigned',
            prettyName: 'Members',
            show: true,
            cell: (info) => {
              let membersAssignedMore = 0
              return (
                <div className='msync-small-profile-avatar-container'>
                  { ( info.row.original.membersAssigned ).map(( memberInfos, index ) => {
                    if ( index > 2 ) {
                      membersAssignedMore = membersAssignedMore + 1
                      return
                    }
                    return <img className='msync-small-profile-avatar-renewal' src={ memberInfos.avatar } alt="Member's avatar" />
                  }) }
                  {
                    membersAssignedMore !== 0 ? <span className='msync-small-avatar-more-members'>+{membersAssignedMore}</span> : ''
                  }
                </div>  
              )
            },
            footer: props => props.column.id,
            header: () => <span>Members</span>,
          },
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              <Actions info={ info.row.original } row={ info.row } />
            ),
            footer: props => props.column.id,
            header: () => <span>Actions</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  async function DismissLicense( productID ) {
    const {
      group_dashboard: { nonce, base }
    } = window
    let url = base + 'membersync/licensing/v1/org/' + teamId + '/license/dismiss'

    const res = await fetch(`${url}`, {
      method: 'POST',
      body: JSON.stringify({
        product_id: productID
      }),
      headers: {
        'X-WP-Nonce': nonce,
        'Content-type': "application/json"
      },
      credentials: 'same-origin',
      processData: false,
      contentType: false,
    })

    if ( !res.ok ) throw Error(res.statusText)

    const resultJSON = await res.json()
    return resultJSON
  }

  const Actions = ({ info, row }) => {
    let productIDInt = parseInt(info.productID)
    let currentProductID = [productIDInt]
    let currentMembersAssigned = []
    const modalId = "msync-licenses-store-" + info.productID
    currentMembersAssigned[info.productID] = info.membersAssigned.length
    return (
      <div className='msync-table-action-column'>
        <Button
          className="msync-primary-btn"
          onClick={ async () => {
            setDismissLoading(true)
            const response = await DismissLicense( info.productID )
            if ( response.success ) {
              setDismissLoading(false)
              refetchLicenses()
            }
          }}
          name="Dismiss"
          isDisabled={ dismissLoading }
        />
        <Button
          className="msync-button--is-style-outline msync-button--is-theme-destructive"
          type="button"
          name="Renew"
          onClick={ () => MicroModal.show(modalId) }
          aria-label="Close this dialog window"
          isDisabled={ modalLoading[ info.productID ] ? true : false }
        />
        <LicenseTableProvider>
          <LicensesStore 
            teamData={ teamData } 
            productRenewed={ { product: currentProductID, membersAssigned: currentMembersAssigned } } 
            infosMembersAssigned={ info.membersAssigned }
            setCurrentModalLoading={(value) => {
              const currentModalLoading = modalLoading
              currentModalLoading[ info.productID ] = value
              setModalLoading([...currentModalLoading])
            }}
            total={row.original.total}
            membersSelection={membersSelection}
          />
        </LicenseTableProvider>
      </div>
    )
  }

  const exportHeaders = getExportHeaders('team-licenses')
  const { teamData } = useContext(TeamContext)

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Licenses table'
        context='Licenses'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
      </TanstackContent>
    </>
  )
}

export default ExpiringLicensesTable
